import React, { useContext, useEffect, useMemo, useState } from "react";
import { Alert, Form } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
  organizerPhysicalCreateBatchApi,
  organizerPhysicalClaimNextApi,
  organizerPhysicalListBatchesApi,
  organizerPhysicalListEventsApi,
  organizerPhysicalListTicketTypesApi,
  organizerPhysicalVoidBatchApi,
} from "../../api/api";
import { handleError } from "../../helpers/handleError";
import SpinLoading from "../../components/spinLoading/SpinLoading";
import { OrganizerHeaderContext } from "../Layout";
import OrganizerPillSelect from "../components/OrganizerPillSelect";
import "../dashboard.css";
import "./physicalTickets.css";

const asId = (v) => (v == null ? "" : String(v));

const PhysicalTickets = () => {
  const { setPageHeader } = useContext(OrganizerHeaderContext);
  const navigate = useNavigate();

  useEffect(() => {
    setPageHeader({
      title: "Entradas presenciales",
      subtitle: "Generá e imprimí entradas físicas para ventas en el evento.",
    });
  }, [setPageHeader]);

  const [events, setEvents] = useState([]);
  const [ticketTypes, setTicketTypes] = useState([]);
  const [batches, setBatches] = useState([]);

  const [eventId, setEventId] = useState("");
  const [ticketTypeId, setTicketTypeId] = useState("");
  const [qty, setQty] = useState(50);
  const [note, setNote] = useState("");
  const [claimBatchId, setClaimBatchId] = useState("");
  const [lastClaim, setLastClaim] = useState(null);

  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const selectedEvent = useMemo(() => events.find((e) => asId(e.id) === asId(eventId)), [events, eventId]);

  const refreshBatches = async (targetEventId) => {
    const eid = asId(targetEventId || eventId);
    if (!eid) return;
    const { data } = await organizerPhysicalListBatchesApi({ eventId: eid });
    setBatches(data?.info || []);
  };

  useEffect(() => {
    const boot = async () => {
      try {
        setLoading(true);
        setErrorMsg("");
        const res = await organizerPhysicalListEventsApi();
        const list = res?.data?.info || [];
        setEvents(list);
        if (list.length === 1) setEventId(asId(list[0].id));
      } catch (e) {
        setErrorMsg("No pudimos cargar tus eventos.");
        handleError(e);
      } finally {
        setLoading(false);
      }
    };
    boot();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const loadForEvent = async () => {
      if (!eventId) return;
      try {
        setLoading(true);
        setErrorMsg("");
        const [tts, bts] = await Promise.all([
          organizerPhysicalListTicketTypesApi(eventId),
          organizerPhysicalListBatchesApi({ eventId }),
        ]);
        setTicketTypes(tts?.data?.info || []);
        setBatches(bts?.data?.info || []);
        setTicketTypeId("");
        setClaimBatchId("");
        setLastClaim(null);
      } catch (e) {
        setErrorMsg("No pudimos cargar los tipos de entrada.");
        handleError(e);
      } finally {
        setLoading(false);
      }
    };
    loadForEvent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const handleCreate = async () => {
    try {
      setLoading(true);
      setErrorMsg("");
      const payload = {
        eventId,
        ticketTypeId: ticketTypeId || null,
        quantity: Number(qty || 0),
        note: note || "",
      };
      const { data } = await organizerPhysicalCreateBatchApi(payload);
      const batchId = data?.info?.batch?.id;
      await refreshBatches(eventId);
      if (batchId) {
        navigate(`/organizer/presencial/print/${batchId}`);
        return;
      }
      navigate("/organizer/presencial");
    } catch (e) {
      const msg = e?.response?.data?.message || "No pudimos crear el lote.";
      setErrorMsg(msg);
      handleError(e);
    } finally {
      setLoading(false);
    }
  };

  const handleVoid = async (batchId) => {
    const ok = window.confirm("¿Anular las entradas reservadas restantes de este lote?");
    if (!ok) return;
    try {
      setLoading(true);
      await organizerPhysicalVoidBatchApi(batchId);
      await refreshBatches(eventId);
    } catch (e) {
      setErrorMsg("No pudimos anular el lote.");
      handleError(e);
    } finally {
      setLoading(false);
    }
  };

  const handleClaimNext = async () => {
    try {
      setLoading(true);
      setErrorMsg("");
      const { data } = await organizerPhysicalClaimNextApi({
        eventId,
        batchId: claimBatchId || null,
      });
      setLastClaim(data?.info || null);
      await refreshBatches(eventId);
    } catch (e) {
      const msg = e?.response?.data?.message || "No pudimos marcar la entrada como vendida.";
      setErrorMsg(msg);
      handleError(e);
    } finally {
      setLoading(false);
    }
  };

  if (loading && events.length === 0) return <SpinLoading />;

  return (
    <div className={`organizer-page organizer-physical ${loading ? "is-fetching" : ""}`}>
      {errorMsg ? (
        <Alert variant="danger" className="mb-3">
          {errorMsg}
        </Alert>
      ) : null}

      <section className="organizer-card organizer-physical-card organizer-physical-card--create zoco-card mb-4">
        <div className="organizer-physical-card__intro">
          <div>
            <h3 className="organizer-card__title zoco-card__title">Crear lote para imprimir</h3>
            <p className="organizer-card__subtitle zoco-card__subtitle">| Generá e imprimí entradas físicas para el evento.</p>
          </div>
          <div className="organizer-physical-card__header-actions">
            {selectedEvent ? (
              <span className="organizer-field-hint zoco-helper me-3">
                Evento <strong>{selectedEvent.name}</strong>
              </span>
            ) : null}
            <button
              type="button"
              className="organizer-btn organizer-btn-sm zoco-btn"
              disabled={!eventId || Number(qty || 0) <= 0 || loading}
              onClick={handleCreate}
            >
              Crear e Imprimir
            </button>
          </div>
        </div>

        <div className="organizer-physical-create-grid">
          <div className="organizer-physical-create-grid__left">
            <Form.Group className="organizer-field-block">
              <OrganizerPillSelect
                placeholder="Seleccioná un evento"
                value={eventId}
                onChange={(val) => setEventId(val ? String(val) : "")}
                options={events.map((e) => ({ value: asId(e.id), label: e.name }))}
              />
            </Form.Group>

            <Form.Group className="organizer-field-block">
              <Form.Label className="organizer-field-label">Tipo de entrada</Form.Label>
              <OrganizerPillSelect
                placeholder="General"
                value={ticketTypeId}
                onChange={(val) => setTicketTypeId(val ? String(val) : "")}
                disabled={!eventId}
                options={[
                  { value: "", label: "General" },
                  ...ticketTypes.map((t) => ({ value: asId(t.id), label: t.name })),
                ]}
              />
            </Form.Group>

            <Form.Group className="organizer-field-block">
              <Form.Label className="organizer-field-label">Cantidad</Form.Label>
              <Form.Control
                className="organizer-field-input zoco-input"
                type="number"
                min={1}
                max={2000}
                value={qty}
                onChange={(e) => setQty(e.target.value)}
                disabled={!eventId}
              />
            </Form.Group>
          </div>

          <div className="organizer-physical-create-grid__right">
            <Form.Group className="organizer-field-block organizer-physical__note">
              <Form.Label className="organizer-field-label">Nota opcional</Form.Label>
              <Form.Control
                as="textarea"
                className="organizer-field-input organizer-physical__note-textarea zoco-textarea"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Puerta 2 / Caja Principal"
                disabled={!eventId}
              />
            </Form.Group>
          </div>
        </div>
      </section>

      <section className="organizer-card organizer-physical-card organizer-physical-card--claim zoco-card mb-4">
        <div className="organizer-physical-card__intro">
          <div>
            <h3 className="organizer-card__title zoco-card__title">Vender (POS externo / sin integración)</h3>
            <p className="organizer-card__subtitle zoco-card__subtitle">
              | Si tu POS no se integra en tiempo real, al momento de la venta marcá una entrada como “vendida” para que el scanner la acepte.
            </p>
          </div>
          <div className="organizer-physical-card__header-actions">
            {lastClaim?.physicalBatchCode ? (
              <span className="organizer-field-hint zoco-helper me-3">
                Entregar <strong>{lastClaim.physicalBatchCode}{lastClaim.physicalSeq ? ` - #${lastClaim.physicalSeq}` : ""}</strong>
              </span>
            ) : null}
            <button
              type="button"
              className="organizer-btn organizer-btn-sm zoco-btn"
              disabled={!eventId || loading}
              onClick={handleClaimNext}
            >
              Entregar siguiente
            </button>
          </div>
        </div>

        <div className="organizer-form-grid organizer-form-grid--two organizer-form-grid--compact">
          <Form.Group className="organizer-field-block">
            <Form.Label className="organizer-field-label">Lote (Opcional)</Form.Label>
            <OrganizerPillSelect
              placeholder="Cualquiera (más antiguo primero)"
              value={claimBatchId}
              onChange={(val) => setClaimBatchId(val ? String(val) : "")}
              disabled={!eventId}
              className={!claimBatchId ? "is-soft-trigger" : ""}
              options={[
                { value: "", label: "Cualquiera (más antiguo primero)" },
                ...batches.map((b) => ({
                  value: asId(b.id),
                  label: `${b.code} - ${b.ticketTypeName || "General"}`,
                })),
              ]}
            />
          </Form.Group>
        </div>
      </section>

      <section className="organizer-card organizer-physical-card organizer-physical-card--history zoco-card">
        <div className="organizer-card__header organizer-physical-card__header">
          <div>
            <h3 className="organizer-card__title zoco-card__title">Lotes creados</h3>
            <p className="organizer-card__subtitle zoco-card__subtitle">| Historial de lotes generados para este evento.</p>
          </div>
        </div>
        {!eventId ? (
          <div className="organizer-physical-empty zoco-empty">Seleccioná un evento para ver sus lotes</div>
        ) : batches.length === 0 ? (
          <div className="organizer-physical-empty zoco-empty">Todavía no hay lotes para este evento.</div>
        ) : (
          <div className="table-responsive organizer-physical-table-wrap">
            <table className="zoco-lotes-table">
              <thead>
                <tr>
                  <th>CÓDIGO</th>
                  <th>TIPO</th>
                  <th>CANTIDAD</th>
                  <th>RESERVADAS</th>
                  <th>VENDIDAS</th>
                  <th>CANJEADAS</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {batches.map((b) => {
                  const counts = b.counts || {};
                  return (
                    <tr key={asId(b.id)} className={`organizer-physical-batch-row organizer-physical-batch-row--${String(b.status || "").toLowerCase()}`}>
                      <td>{b.code}</td>
                      <td>{b.ticketTypeName || "General"}</td>
                      <td>{b.quantity}</td>
                      <td>{counts.reserved ?? "-"}</td>
                      <td>{counts.issued ?? "-"}</td>
                      <td>{counts.redeemed ?? "-"}</td>
                      <td>
                        <div className="zoco-lotes-table__actions">
                          <button
                            type="button"
                            className="organizer-btn organizer-btn-sm zoco-btn zoco-btn--ghost"
                            onClick={() => navigate(`/organizer/presencial/print/${asId(b.id)}`)}
                            disabled={String(b.status || "").toUpperCase() === "VOID"}
                          >
                            IMPRIMIR
                          </button>
                          <button
                            type="button"
                            className="organizer-btn organizer-btn-sm zoco-btn zoco-btn--danger"
                            onClick={() => handleVoid(asId(b.id))}
                            disabled={String(b.status || "").toUpperCase() === "VOID"}
                          >
                            ANULAR
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default PhysicalTickets;
